import { useEffect, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { User } from '@state/index';
import { BroadcastChannel } from 'broadcast-channel';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import useTheme from '@hooks/useTheme';
import { clearCsrfTokenForUser } from '@tools/csrf';
import getConfig from 'next/config';
import { fetchClientWithTimeout } from '@tools/fetchClient';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import useNotifications from './useNotifications';
import useMeQuery from './useMeQuery';

const { publicRuntimeConfig } = getConfig();
const LOG = LoggerFactory.create('useLogout');

const logout = async ({ router, event, attendeeId, logoutPageData }) => {
  const headers = {
    'X-Cvent-CSRF': localStorage.getItem('csrfToken') || sessionStorage.getItem('csrfToken')
  };

  try {
    fetchClientWithTimeout.fetch(`${publicRuntimeConfig.BASE_PATH}/api/logout?eventId=${event?.id}`, {
      headers,
      method: 'POST'
    });
  } catch (error) {
    LOG.error(`Error logging user ${attendeeId} out of event ${event?.id}`, error);
  }

  if (event?.links?.summary) {
    window.location.href = event.links.summary;
  } else {
    // Store data needed for logout page from logoutPageData
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('event-logout-page-info', JSON.stringify(logoutPageData));
    }
    clearCsrfTokenForUser(attendeeId);
    sessionStorage.removeItem(`${event?.id}-sessionListFilters`);
    router.push(`/events/${event?.id}/logout`);
  }
};

const useLogout = () => {
  const router = useRouter();
  const { revokePubNubAuthKey } = useNotifications();
  const { data } = useMeQuery();
  const event = data?.me?.event;
  const eventId = event?.id;
  const attendeeId = data?.me?.id;
  const { palette } = useTheme();
  const [revokeGetStreamJWT] = useMutation(User.Mutations.LOGOUT);
  const { isFeatureSettingEnabled } = useAttendeeHubConfig();

  const hasLandingPage = isFeatureSettingEnabled('HAS_PUBLIC_LANDING_PAGE');

  const logoutPageData = useMemo(
    () => ({
      eventIdentification: eventId,
      loginBackgroundImage: event?.appConfig?.webConfig?.loginBackgroundImage,
      loginBackgroundImageAltText: event?.appConfig?.webConfig?.loginBackgroundImageAltText,
      primarySeedColor: palette?.primary.seedColor,
      cventPrivacyPolicyText: data?.me?.event?.cventPrivacyPolicyText,
      eventPrivacyPolicy: data?.me?.event?.links?.privacyPolicy,
      // If the event has a public landing page, redirect to /events/{eventId}/landing; otherwise, redirect to /events/{eventId}
      loginUrl: hasLandingPage
        ? `${publicRuntimeConfig.BASE_PATH}/events/${eventId}/landing`
        : `${publicRuntimeConfig.BASE_PATH}/events/${eventId}`
    }),
    [
      data?.me?.event?.cventPrivacyPolicyText,
      data?.me?.event?.links?.privacyPolicy,
      event?.appConfig?.webConfig?.loginBackgroundImage,
      event?.appConfig?.webConfig?.loginBackgroundImageAltText,
      eventId,
      palette?.primary.seedColor
    ]
  );

  // Setup BroadcastChannel to listen for other windows/tabs logging out
  const channel = useMemo(() => {
    if (typeof window !== 'undefined') {
      return new BroadcastChannel('logout');
    }
  }, []);

  const revokingPubNubAuthKey = useCallback(async () => {
    try {
      await revokePubNubAuthKey();
    } catch (err) {
      LOG.error(`Error revoking pubNub authKey for eventId: ${eventId}`, err);
    }
  }, [revokePubNubAuthKey, eventId]);

  // If another tab logs out of this event, also log out
  const onMessage = useCallback(
    async (otherTabEventId: string) => {
      if (otherTabEventId === event?.id) {
        await revokingPubNubAuthKey();
        logout({
          router,
          event,
          attendeeId,
          logoutPageData
        });
      }
    },
    [event, revokingPubNubAuthKey, router, logoutPageData]
  );

  // Listen for logout broadcasts
  useEffect(() => {
    if (typeof window !== 'undefined') {
      channel.addEventListener('message', onMessage);
      return () => {
        channel.removeEventListener('message', onMessage);
      };
    }

    return null;
  }, [channel, onMessage]);

  return async () => {
    channel.postMessage(event?.id);
    revokeGetStreamJWT({
      variables: {
        input: {
          revokeTokensIssuedBefore: new Date(Date.now()).toISOString()
        }
      }
    });
    await revokingPubNubAuthKey();
    await logout({
      router,
      event,
      attendeeId,
      logoutPageData
    });
  };
};

export default useLogout;
